import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { usePolling } from "../hooks/usePolling";
import { ArrowLeft, IdentificationBadge, ArrowCounterClockwise, Clock, ClipboardText, PencilSimple, X, Check } from "@phosphor-icons/react";
import { formatET } from "../lib/dateUtils";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { RepSessionClock } from "../components/shared/RepSessionClock";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LeadRecordModal } from "../components/shared/LeadRecordModal";
import { ContactsSparkline } from "../components/shared/ContactsSparkline";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

const STATUS_STYLE = {
  pending: "bg-igc-gold/10 text-igc-goldText border-igc-gold/20",
  completed: "bg-igc-success/10 text-igc-success border-igc-success/30",
  missed: "bg-igc-danger/10 text-igc-danger border-igc-danger/40",
};

// Item 2 (Neo Build, vince_manager_upgrades, Sep 23 2026) — manager-view-only disposition filter,
// options are the real values already logged via DispositionModal.jsx (no invented buckets).
const DISPOSITION_FILTER_OPTIONS = [
  "No Answer", "Left Voicemail", "Called Wrong Number", "Not Interested",
  "Interested — Schedule Callback", "Submitted Application", "Deal Closed", "Other",
];

export default function RepDetailDrilldownPage() {
  const { repId } = useParams();
  const navigate = useNavigate();
  const isAdmin = window.location.pathname.startsWith("/admin");
  const apiBase = isAdmin ? "/admin" : "/manager";
  const backPath = isAdmin ? "/admin/rep-activity" : "/manager?tab=downline";
  const [data, setData] = useState(null);
  const [resettingId, setResettingId] = useState(null);
  const [notesLead, setNotesLead] = useState(null);
  const [callLogDate, setCallLogDate] = useState(new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" }));
  const [callLog, setCallLog] = useState(null);
  const [callLogLoading, setCallLogLoading] = useState(false);
  const [weeklyGrades, setWeeklyGrades] = useState(null);
  const [editingContact, setEditingContact] = useState(false);
  const [contactForm, setContactForm] = useState({ email: "", phone: "" });
  const [savingContact, setSavingContact] = useState(false);
  const [sparkline, setSparkline] = useState(null);
  const [dispositionFilter, setDispositionFilter] = useState("all");
  const { user } = useAuth();

  useEffect(() => {
    api.get(`${apiBase}/reps/${repId}/contacts-sparkline`).then(({ data }) => setSparkline(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repId]);

  const loadDetail = () => {
    api.get(`${apiBase}/reps/${repId}/detail`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  useEffect(() => {
    loadDetail();
  }, [repId, apiBase]);

  usePolling(loadDetail, 30000);

  const loadCallLog = (dateStr) => {
    setCallLogLoading(true);
    api.get(`${apiBase}/reps/${repId}/daily-call-log`, { params: { date: dateStr } })
      .then(({ data }) => setCallLog(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setCallLogLoading(false));
  };

  const loadWeeklyGrades = () => {
    api.get(`${apiBase}/reps/${repId}/weekly-grades`)
      .then(({ data }) => setWeeklyGrades(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  const resetLead = async (leadId) => {
    setResettingId(leadId);
    try {
      await api.post(`${apiBase}/leads/${leadId}/reset`);
      toast.success("Lead reset — it's back to unrevealed");
      loadDetail();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setResettingId(null);
    }
  };

  const openEditContact = () => {
    setContactForm({ email: data.rep.email || "", phone: data.rep.phone || "" });
    setEditingContact(true);
  };

  const saveContact = async () => {
    setSavingContact(true);
    try {
      const newEmail = contactForm.email.trim();
      const newPhone = contactForm.phone.trim();
      if (newEmail !== (data.rep.email || "")) await api.patch(`${apiBase}/reps/${repId}/email`, { email: newEmail });
      if (newPhone !== (data.rep.phone || "")) await api.patch(`${apiBase}/reps/${repId}/phone`, { phone: newPhone });
      toast.success("Contact info updated");
      setEditingContact(false);
      loadDetail();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      loadDetail();
    } finally {
      setSavingContact(false);
    }
  };

  const navItems = [{ to: backPath, label: "Back", icon: <IdentificationBadge size={18} weight="duotone" />, testId: "nav-rep-detail-back" }];

  if (!data) return null;
  const { rep, leads, call_log, callbacks, deals, weekly_summary, session_history } = data;
  const filteredLeads = dispositionFilter === "all" ? leads : leads.filter((l) => l.latest_disposition === dispositionFilter);

  const formatDuration = (seconds) => {
    if (seconds == null) return "—";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Rep Detail" moduleBackPath={backPath}>
      <div data-testid="rep-detail-drilldown-page">
        <button data-testid="rep-detail-back-button" onClick={() => navigate(backPath)} className="flex items-center gap-2 text-igc-muted hover:text-igc-goldText text-sm mb-4">
          <ArrowLeft size={16} /> Back
        </button>
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1" title="This rep's full activity history — leads, calls, deals, and login sessions.">{rep.display_name} <span className="text-igc-goldText text-xl">({rep.user_id})</span></h1>
        <p className="text-igc-muted text-sm mb-1">Manager: {rep.manager_display_name || "Unassigned"}</p>
        {!editingContact ? (
          <p className="text-igc-muted text-sm mb-4 flex items-center gap-2 flex-wrap" data-testid="rep-detail-profile-info">
            <span>
              {rep.first_name || rep.last_name ? `${rep.first_name || ""} ${rep.last_name || ""}`.trim() + " — " : ""}{rep.email || "no email on file"} — {rep.phone || "no phone on file"} — Joined {rep.date_joined ? formatET(rep.date_joined, "MMM d, yyyy") : "—"}
            </span>
            <button
              data-testid="rep-detail-edit-contact-button"
              onClick={openEditContact}
              title="Update this rep's email or phone number"
              className="flex items-center gap-1 text-[11px] text-igc-muted hover:text-igc-goldText border border-igc-gold/30 px-2 py-1 rounded-full transition-colors"
            >
              <PencilSimple size={11} weight="duotone" /> Edit Contact Info
            </button>
          </p>
        ) : (
          <div className="bg-igc-surface border border-igc-gold/20 p-4 mb-4 max-w-xl flex flex-col sm:flex-row items-stretch sm:items-end gap-3" data-testid="rep-detail-contact-edit-form">
            <div className="flex-1">
              <label className="text-igc-muted text-[10px] uppercase tracking-widest mb-1 block">Email</label>
              <input
                data-testid="rep-detail-edit-email-input"
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                title="This rep's contact email."
                className="w-full bg-igc-charcoal border border-igc-gold/15 px-3 py-2 text-sm text-igc-cream"
              />
            </div>
            <div className="flex-1">
              <label className="text-igc-muted text-[10px] uppercase tracking-widest mb-1 block">Phone</label>
              <input
                data-testid="rep-detail-edit-phone-input"
                type="tel"
                value={contactForm.phone}
                onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                title="This rep's contact phone number."
                className="w-full bg-igc-charcoal border border-igc-gold/15 px-3 py-2 text-sm text-igc-cream"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="rep-detail-save-contact-button"
                onClick={saveContact}
                disabled={savingContact}
                title="Saves this rep's updated email and phone."
                className="flex items-center gap-1 text-xs px-3 py-2 bg-igc-gold text-igc-charcoal font-medium hover:bg-igc-gold/90 transition-colors disabled:opacity-50"
              >
                <Check size={13} weight="bold" /> {savingContact ? "Saving…" : "Save"}
              </button>
              <button
                data-testid="rep-detail-cancel-contact-button"
                onClick={() => setEditingContact(false)}
                disabled={savingContact}
                title="Discards these changes without saving."
                className="flex items-center gap-1 text-xs px-3 py-2 border border-igc-gold/30 text-igc-muted hover:text-igc-cream transition-colors"
              >
                <X size={13} weight="bold" /> Cancel
              </button>
            </div>
          </div>
        )}

        <div className="bg-igc-surface border border-igc-gold/15 px-4 py-3 mb-6 max-w-md" data-testid="rep-detail-session-clock">
          <p className="text-igc-muted text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1">
            <Clock size={12} weight="bold" /> Current Session
          </p>
          <RepSessionClock session={rep.session} testId="rep-detail-session-clock-display" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4 mb-4">
          <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-detail-remaining" title="How many leads still need a disposition, out of the total assigned."><p className="font-serif text-xl text-igc-cream">{rep.leads_remaining}/{rep.total_assigned}</p><p className="text-igc-muted text-xs uppercase tracking-widest">Remaining (Disposition)</p></div>
          <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-detail-calls-remaining" title="Leads assigned to this rep that have never been called even once yet — drops the instant the rep clicks Call."><p className="font-serif text-xl text-igc-cream">{rep.calls_remaining}</p><p className="text-igc-muted text-xs uppercase tracking-widest">Remaining Calls</p></div>
          <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-detail-calls-today" title="How many calls this rep has made today."><p className="font-serif text-xl text-igc-cream">{rep.calls_attempted_today}</p><p className="text-igc-muted text-xs uppercase tracking-widest">Calls Attempted Today</p></div>
          <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-detail-calls-week" title="How many calls this rep has made this week."><p className="font-serif text-xl text-igc-cream">{rep.calls_attempted_week}</p><p className="text-igc-muted text-xs uppercase tracking-widest">Calls This Week</p></div>
          <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-detail-calls-total" title="Every call this rep has ever made, all time."><p className="font-serif text-xl text-igc-cream">{rep.calls_attempted_total}</p><p className="text-igc-muted text-xs uppercase tracking-widest">Calls Attempted (All Time)</p></div>
          <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-detail-sweep-calls" title="A Sweep Call is any call made on a lead the rep has already called at least once before."><p className="font-serif text-xl text-igc-cream">{rep.sweep_calls_total}</p><p className="text-igc-muted text-xs uppercase tracking-widest">Sweep Calls (All Time)</p></div>
          <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-detail-unique-called" title="How many distinct leads this rep has reached at least once, ever."><p className="font-serif text-xl text-igc-cream">{rep.unique_leads_called_alltime}</p><p className="text-igc-muted text-xs uppercase tracking-widest">Unique Leads Called</p></div>
          <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-detail-deals" title="Deals this rep has personally submitted for commission."><p className="font-serif text-xl text-igc-cream">{rep.deals_submitted}</p><p className="text-igc-muted text-xs uppercase tracking-widest">Personal Referrals</p></div>
          <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-detail-commissions" title="Total commission this rep has earned to date."><p className="font-serif text-xl text-igc-goldText">${rep.commissions_earned?.toLocaleString?.() ?? rep.commissions_earned}</p><p className="text-igc-muted text-xs uppercase tracking-widest">Commissions Earned</p></div>
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-4 mb-6 max-w-md" data-testid="rep-detail-contacts-sparkline-section" title="One data point per day — calls placed by this rep, last 7 days. Admin/manager-visible only.">
          <p className="text-igc-muted text-[10px] uppercase tracking-widest mb-2">Contacts Made — Last 7 Days</p>
          {sparkline ? <ContactsSparkline data={sparkline} testId="rep-detail-contacts-sparkline" /> : <p className="text-igc-muted text-xs">Loading…</p>}
        </div>

        <p className="text-igc-muted text-[11px] mb-8" data-testid="rep-detail-call-count-note">
          Every "Calls" number on every screen (rep cards, scorecards, this page) pulls from the exact same source — the only difference is the time window (Today / This Week / All Time). "Calls Attempted" counts every dial (repeat calls to the same lead count each time). "Unique Leads Called" counts distinct leads reached at least once. "Sweep Calls" is Calls Attempted minus Unique Leads Called — every redial on a lead already called before. "Remaining Calls" is leads assigned minus Unique Leads Called (All Time) — it drops the instant a call is logged, independent of dispositions.
        </p>

        <div className="mb-8" data-testid="rep-detail-weekly-summary">
          <h2 className="font-serif text-lg text-igc-cream tracking-tight mb-3">This Week's Activity <span className="text-igc-muted text-xs font-sans">(Mon–Sun)</span></h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {weekly_summary?.map((d) => (
              <div
                key={d.date}
                data-testid={`rep-detail-weekly-day-${d.date}`}
                className={`border p-3 ${d.is_today ? "bg-igc-gold/10 border-igc-gold/40" : "bg-igc-surface border-igc-gold/15"}`}
              >
                <p className={`text-xs uppercase tracking-widest mb-2 ${d.is_today ? "text-igc-goldText" : "text-igc-muted"}`}>{d.day_label}{d.is_today ? " • Today" : ""}</p>
                <div className="space-y-1 text-igc-cream text-xs">
                  <p data-testid={`rep-detail-weekly-calls-${d.date}`}>Calls: <span className="text-igc-goldText">{d.calls_made}</span></p>
                  <p data-testid={`rep-detail-weekly-opened-${d.date}`}>Opened: <span className="text-igc-goldText">{d.leads_opened}</span></p>
                  <p data-testid={`rep-detail-weekly-referrals-${d.date}`}>Referrals: <span className="text-igc-goldText">{d.personal_referrals}</span></p>
                  <p data-testid={`rep-detail-weekly-commissions-${d.date}`}>Comm: <span className="text-igc-goldText">${d.commissions_earned.toLocaleString()}</span></p>
                  <p className="pt-1 flex gap-2 flex-wrap">
                    <span className="text-igc-success" data-testid={`rep-detail-weekly-won-${d.date}`}>W:{d.deals_won}</span>
                    <span className="text-igc-muted" data-testid={`rep-detail-weekly-pending-${d.date}`}>P:{d.deals_pending}</span>
                    <span className="text-igc-danger" data-testid={`rep-detail-weekly-lost-${d.date}`}>L:{d.deals_lost}</span>
                  </p>
                  {d.conversion_rate != null && (
                    <p className="text-igc-goldText/80" data-testid={`rep-detail-weekly-conversion-${d.date}`}>Conv: {d.conversion_rate}%</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Tabs defaultValue="leads">
          <TabsList className="bg-igc-surface border border-igc-gold/15 mb-6">
            <TabsTrigger data-testid="rep-detail-tab-leads" value="leads" title="Every lead currently assigned to this rep." className="text-igc-muted data-[state=active]:bg-igc-gold/15 data-[state=active]:text-igc-goldText">Leads</TabsTrigger>
            <TabsTrigger data-testid="rep-detail-tab-call-log" value="call-log" title="Every disposition this rep has ever logged, across all leads." className="text-igc-muted data-[state=active]:bg-igc-gold/15 data-[state=active]:text-igc-goldText">Call Log</TabsTrigger>
            <TabsTrigger data-testid="rep-detail-tab-daily-call-log" value="daily-call-log" onClick={() => !callLog && loadCallLog(callLogDate)} title="Pick any single day and see exactly which calls this rep made." className="text-igc-muted data-[state=active]:bg-igc-gold/15 data-[state=active]:text-igc-goldText">Daily Call Log</TabsTrigger>
            <TabsTrigger data-testid="rep-detail-tab-weekly-grades" value="weekly-grades" onClick={() => !weeklyGrades && loadWeeklyGrades()} title="Weekly performance snapshots automatically captured every Monday." className="text-igc-muted data-[state=active]:bg-igc-gold/15 data-[state=active]:text-igc-goldText">Weekly Grades</TabsTrigger>
            <TabsTrigger data-testid="rep-detail-tab-callbacks" value="callbacks" title="Every callback this rep has scheduled with a lead." className="text-igc-muted data-[state=active]:bg-igc-gold/15 data-[state=active]:text-igc-goldText">Callback Calendar</TabsTrigger>
            <TabsTrigger data-testid="rep-detail-tab-deals" value="deals" title="Every deal this rep has submitted." className="text-igc-muted data-[state=active]:bg-igc-gold/15 data-[state=active]:text-igc-goldText">Deals</TabsTrigger>
            <TabsTrigger data-testid="rep-detail-tab-sessions" value="sessions" title="Every time this rep has logged in and out, with session duration." className="text-igc-muted data-[state=active]:bg-igc-gold/15 data-[state=active]:text-igc-goldText">Login/Logout History</TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            {!isAdmin && (
              <div className="flex items-center gap-2 mb-3" data-testid="rep-detail-disposition-filter-wrap" title="Manager-only filter — narrows this rep's lead list by their latest logged disposition. Does not change the data, just what's shown.">
                <span className="text-igc-muted text-xs uppercase tracking-widest">Disposition</span>
                <Select value={dispositionFilter} onValueChange={setDispositionFilter}>
                  <SelectTrigger data-testid="rep-detail-disposition-filter-select" className="w-56 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectItem value="all">All</SelectItem>
                    {DISPOSITION_FILTER_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {filteredLeads.length === 0 && <p className="text-igc-muted text-sm" data-testid="rep-detail-leads-empty">{leads.length === 0 ? "No leads assigned yet." : "No leads match this disposition."}</p>}
            <div className="space-y-2">
              {filteredLeads.map((l) => (
                <div key={l.id} className="bg-igc-surface border border-igc-gold/15 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`rep-detail-lead-${l.id}`}>
                  <div>
                    <p className="text-igc-cream text-sm">{l.name} — {l.phone}</p>
                    <p className="text-igc-muted text-xs">{l.source} — {formatET(l.created_at, "MMM d, yyyy")}</p>
                    {l.latest_disposition ? (
                      <p className="text-igc-muted text-[11px] mt-1" data-testid={`rep-detail-lead-disposition-${l.id}`} title={l.latest_disposition_notes || "No notes logged on this disposition"}>
                        <span className="text-igc-goldText">{l.latest_disposition}</span> — logged {formatET(l.latest_disposition_at, "MMM d, yyyy h:mm a")}
                        {l.latest_disposition_notes && <span> · "{l.latest_disposition_notes}"</span>}
                      </p>
                    ) : (
                      <p className="text-igc-muted text-[11px] mt-1" data-testid={`rep-detail-lead-disposition-${l.id}`}>No disposition logged yet</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-2 py-1 bg-igc-gold/10 text-igc-goldText uppercase">{l.stage === "Contacted" ? "Contacted" : l.phone_revealed_at ? "Called" : "Not Called"}</span>
                    <button
                      data-testid={`rep-detail-lead-notes-${l.id}`}
                      onClick={() => setNotesLead(l)}
                      title="Opens this lead's notes and outreach history."
                      className="flex items-center gap-1 text-[11px] text-igc-muted hover:text-igc-goldText border border-igc-gold/30 px-2 py-1 rounded-full transition-colors"
                    >
                      <ClipboardText size={11} weight="duotone" /> Notes
                    </button>
                    {l.phone_revealed_at && (
                      <button
                        data-testid={`rep-detail-lead-reset-${l.id}`}
                        onClick={() => resetLead(l.id)}
                        disabled={resettingId === l.id}
                        title="Puts this lead back to 'unrevealed' so it counts as a fresh call again."
                        className="flex items-center gap-1 text-[11px] text-igc-muted hover:text-igc-goldText border border-igc-gold/30 px-2 py-1 rounded-full transition-colors disabled:opacity-50"
                      >
                        <ArrowCounterClockwise size={11} weight="bold" /> {resettingId === l.id ? "Resetting…" : "Reset"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="call-log">
            {call_log.length === 0 && <p className="text-igc-muted text-sm" data-testid="rep-detail-call-log-empty">No dispositions logged yet.</p>}
            <div className="space-y-2">
              {call_log.map((c) => (
                <div key={c.id} className="bg-igc-surface border border-igc-gold/15 p-3" data-testid={`rep-detail-call-log-${c.id}`}>
                  <p className="text-igc-cream text-sm">{c.lead_name} — <span className="text-igc-goldText">{c.result}</span></p>
                  <p className="text-igc-muted text-xs mt-1">{c.notes}</p>
                  <p className="text-igc-muted text-[11px] mt-1">{formatET(c.timestamp, "MMM d, yyyy h:mm a")} ET</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="daily-call-log">
            <div className="flex items-center gap-3 mb-4">
              <input
                type="date"
                data-testid="rep-detail-daily-call-log-date-picker"
                value={callLogDate}
                onChange={(e) => { setCallLogDate(e.target.value); loadCallLog(e.target.value); }}
                title="Pick a date to see exactly which calls this rep made that day."
                className="bg-igc-surface border border-igc-gold/20 text-igc-cream text-sm px-3 py-2"
              />
              {callLog && (
                <p className="text-igc-muted text-xs" data-testid="rep-detail-daily-call-log-summary">
                  {callLog.total_calls} call{callLog.total_calls === 1 ? "" : "s"} · {callLog.unique_leads_called} unique lead{callLog.unique_leads_called === 1 ? "" : "s"} · {callLog.sweep_calls} sweep call{callLog.sweep_calls === 1 ? "" : "s"}
                </p>
              )}
            </div>
            {callLogLoading && <p className="text-igc-muted text-sm" data-testid="rep-detail-daily-call-log-loading">Loading…</p>}
            {!callLogLoading && callLog?.calls?.length === 0 && <p className="text-igc-muted text-sm" data-testid="rep-detail-daily-call-log-empty">No calls logged on this day.</p>}
            <div className="space-y-2">
              {callLog?.calls?.map((c, i) => (
                <div key={`${c.lead_id}-${i}`} className="bg-igc-surface border border-igc-gold/15 p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`rep-detail-daily-call-${c.lead_id}-${i}`}>
                  <div>
                    <p className="text-igc-cream text-sm">{c.lead_name} <span className="text-igc-muted text-xs">— {c.time_et} ET</span></p>
                    {c.dispositions.map((d, di) => (
                      <p key={di} className="text-igc-muted text-xs mt-1">{d.time_et} ET — <span className="text-igc-goldText">{d.result}</span>: {d.notes}</p>
                    ))}
                  </div>
                  {c.is_sweep ? (
                    <span className="text-[10px] px-2 py-1 bg-amber-500/10 text-amber-600 uppercase tracking-wide" title="A Sweep Call is any call made on a lead the rep has already called at least once before." data-testid={`rep-detail-daily-call-sweep-${c.lead_id}-${i}`}>Sweep Call</span>
                  ) : (
                    <span className="text-[10px] px-2 py-1 bg-igc-gold/10 text-igc-goldText uppercase tracking-wide" data-testid={`rep-detail-daily-call-first-${c.lead_id}-${i}`}>First Attempt</span>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="weekly-grades">
            {(!weeklyGrades || weeklyGrades.length === 0) && <p className="text-igc-muted text-sm" data-testid="rep-detail-weekly-grades-empty">No weekly grade snapshots yet — the first one captures automatically this coming Monday.</p>}
            <div className="space-y-2">
              {weeklyGrades?.map((w) => (
                <div key={w.id} className="bg-igc-surface border border-igc-gold/15 p-4 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid={`rep-detail-weekly-grade-${w.id}`}>
                  <div><p className="text-igc-muted text-[10px] uppercase tracking-widest">Week Of</p><p className="text-igc-cream text-sm">{w.week_of}</p></div>
                  <div><p className="text-igc-muted text-[10px] uppercase tracking-widest">Attempted Calls</p><p className="text-igc-cream text-sm">{w.attempted_calls}</p></div>
                  <div><p className="text-igc-muted text-[10px] uppercase tracking-widest">Sweep Calls</p><p className="text-igc-cream text-sm">{w.sweep_calls}</p></div>
                  <div><p className="text-igc-muted text-[10px] uppercase tracking-widest">Unique Leads Called</p><p className="text-igc-cream text-sm">{w.unique_leads_called}</p></div>
                  <div><p className="text-igc-muted text-[10px] uppercase tracking-widest">Personal Referrals</p><p className="text-igc-cream text-sm">{w.personal_referrals}</p></div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="callbacks">
            {callbacks.length === 0 && <p className="text-igc-muted text-sm" data-testid="rep-detail-callbacks-empty">No callbacks scheduled.</p>}
            <div className="space-y-2">
              {callbacks.map((c) => (
                <div key={c.id} className={`p-3 border flex items-center justify-between ${c.status === "missed" ? "bg-igc-danger/5 border-igc-danger/40" : "bg-igc-surface border-igc-gold/15"}`} data-testid={`rep-detail-callback-${c.id}`}>
                  <div><p className="text-igc-cream text-sm">{c.lead_name}</p><p className="text-igc-muted text-xs">{formatET(c.scheduled_at, "MMM d, yyyy h:mm a")} ET</p></div>
                  <span className={`text-[10px] px-2 py-1 uppercase border ${STATUS_STYLE[c.status]}`}>{c.status}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="deals">
            {deals.length === 0 && <p className="text-igc-muted text-sm" data-testid="rep-detail-deals-empty">No deals submitted yet.</p>}
            <div className="space-y-2">
              {deals.map((d) => (
                <div key={d.id} className="bg-igc-surface border border-igc-gold/15 p-3 flex items-center justify-between" data-testid={`rep-detail-deal-${d.id}`}>
                  <div><p className="text-igc-cream text-sm">{d.name}</p><p className="text-igc-muted text-xs">{d.source} — {formatET(d.created_at, "MMM d, yyyy")}</p></div>
                  <span className="text-[10px] px-2 py-1 bg-igc-gold/10 text-igc-goldText uppercase">{d.stage}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="sessions">
            {(!session_history || session_history.length === 0) && <p className="text-igc-muted text-sm" data-testid="rep-detail-sessions-empty">No login history yet.</p>}
            <div className="space-y-2">
              {session_history?.map((s) => (
                <div key={s.id} className="bg-igc-surface border border-igc-gold/15 p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`rep-detail-session-${s.id}`}>
                  <div>
                    <p className="text-igc-cream text-sm">{formatET(s.login_at, "MMM d, yyyy")}</p>
                    <p className="text-igc-muted text-xs">In: {formatET(s.login_at, "h:mm a")} — Out: {s.logout_at ? formatET(s.logout_at, "h:mm a") : "still online"}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-1 uppercase ${s.active ? "bg-igc-success/10 text-igc-success" : "bg-igc-gold/10 text-igc-goldText"}`}>
                    {s.active ? "Active now" : formatDuration(s.duration_seconds)}
                  </span>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      {notesLead && (
        <LeadRecordModal lead={notesLead} currentUserId={user?.id} userRole={user?.role} onClose={() => setNotesLead(null)} />
      )}
    </DashboardLayout>
  );
}
